import React, { useState, useEffect, useRef, useMemo, useContext, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import './i18n';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  BookText, Settings, Moon, Sun, Library, LogOut,
  UserCircle, Cloud, ShieldCheck, HardDrive, Info, LogIn, Loader2,
  Menu, X, Sparkles, BookOpen, Headphones, Brain, BrainCircuit, Mic, Mic2, Edit3, PenLine,
  Users, UsersRound, Fingerprint, Trophy, CheckCircle2, RotateCw,
  MessageCircle, BarChart2, ChevronLeft, Volume2, Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HomePage from './pages/HomePage';
import ReaderPage from './pages/ReaderPage';
import FlashcardsPage from './pages/FlashcardsPage';
import LibriTuiReaderPage from './pages/LibriTuiReaderPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import LibriTuiLibraryPage from './pages/LibriTuiLibraryPage';
import PreviewReader from './pages/PreviewReader';
import FriendsPage from './pages/FriendsPage';
import CommunityPage from './pages/CommunityPage';
import AudiobooksPage from './audiobooks/AudiobooksPage';
import AudiobookDetailPage from './audiobooks/AudiobookDetailPage';
import PronunciationPage from './pages/PronunciationPage';
import WritingPage from './pages/WritingPage';
import ListeningPage from './pages/ListeningPage';
import SharingPage from './pages/SharingPage';
import AchievementsPage from './pages/AchievementsPage';
import HomeOrbMenu from './pages/HomeOrbMenu';
import HubPage, { HubOption } from './pages/HubPage';
import { COMMUNITY_ICONS } from './components/CommunityLinks';
import ChatPage from './pages/ChatPage';
import CommunityFeedPage from './pages/CommunityFeedPage';
import ActiveVoicePage from './pages/ActiveVoicePage';
import VozAtivaPage from './pages/VozAtivaPage';
import FlashcardStorePage from './pages/FlashcardStorePage';
import FlashcardStoreSelectPage from './pages/FlashcardStoreSelectPage';
import StatisticsPage from './pages/StatisticsPage';
import NotificationsPage from './pages/NotificationsPage';
import GrammarPage from './pages/GrammarPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LevelTestPage from './pages/LevelTestPage';
import HelpPage from './pages/HelpPage';
import OnboardingPage from './pages/OnboardingPage';
import RoadmapPage from './pages/RoadmapPage';
import ReviewErrorsPage from './pages/ReviewErrorsPage';
import ErrorReviewFloatingButton from './components/ErrorReviewFloatingButton';
import BetaSyncPage from './pages/BetaSyncPage';
import ShadowingPage from './pages/ShadowingPage';
import { ReportsPage } from './pages/ReportsPage';
import { PeriodReportCycleNotification } from './components/PeriodReportCycleNotification';
import { activeStudyTracker } from './services/activeStudyTrackerService';
import { ThemeContextType, ThemeMode, AuthContextType, User, AccentColor, TranslationMode, PreferredTranslator } from './types';
import { DataProvider, useData } from './contexts/DataContext';
import AvatarRenderer from './components/AvatarRenderer';

import { BottomNavigationBar } from './components/BottomNavigationBar';
import ReloadPrompt from './ReloadPrompt';
import SecurityGuard from './components/SecurityGuard';
import { MissionProgressBar } from './components/MissionProgressBar';
import { useVersionCheck } from './hooks/useVersionCheck';
import { googleDriveService } from './services/googleDriveService';
import { supabase } from './services/supabaseClient';
import { APP_VERSION } from './constants';
import UpdateSuccessModal from './components/UpdateSuccessModal';
import { performHardReset } from './utils/updateUtils';
import { profileService } from './services/profileService';
import { streakService } from './services/streakService';
import { notificationService } from './services/notificationService';
import { tourService } from './services/tourService';

import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SyncedAudiobookProvider } from './contexts/SyncedAudiobookContext';
import { UnifiedDictionaryProvider } from './contexts/UnifiedDictionaryContext';

import LanguagePickerModal from './components/LanguagePickerModal';
import StreakCongratulationModal from './components/StreakCongratulationModal';
import { TourOverlay } from './components/TourOverlay';
import { GlobalErrorBoundary, LoadingOrError } from './components/GlobalErrorBoundary';

const OfflineTranslatorPromptModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handlePrompt = () => setIsOpen(true);
    window.addEventListener('translation_offline_prompt', handlePrompt);
    return () => window.removeEventListener('translation_offline_prompt', handlePrompt);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-5 animate-in zoom-in-95">
        <div className="w-12 h-12 rounded-2xl bg-tint-butter text-warn flex items-center justify-center mx-auto">
          <Download size={24} />
        </div>
        <div className="text-center space-y-2">
          <h4 className="text-lg font-medium text-ink">
            Tradução indisponível
          </h4>
          <p className="text-xs text-slate-500 font-medium leading-relaxed">
            Houve um erro ao tentar traduzir esta frase. Deseja baixar o motor de tradução offline?
          </p>
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => setIsOpen(false)}
            className="flex-1 py-3 px-4 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/settings/translation');
            }}
            className="flex-1 py-3 px-4 rounded-xl bg-ink text-surface font-bold text-xs hover:bg-primary/90 transition-all cursor-pointer"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

const GlobalLoadingOverlay: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [messageKey, setMessageKey] = useState('');
  const [fallbackMessage, setFallbackMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [showCloseBtn, setShowCloseBtn] = useState(false);
  const { t } = useTranslation();
  
  useEffect(() => {
    const handleShow = (e: any) => {
      setMessageKey(e.detail.messageKey);
      setFallbackMessage(e.detail.fallback);
      setProgress(0);
      setShowCloseBtn(false);
      setIsVisible(true);
    };
    window.addEventListener('show-global-loading', handleShow);
    return () => window.removeEventListener('show-global-loading', handleShow);
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 3000;
      const interval = 20; 
      const totalSteps = duration / interval;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const newProgress = (currentStep / totalSteps) * 100;
        
        if (newProgress >= 100) {
          setProgress(100);
          clearInterval(timer);
          setTimeout(() => setShowCloseBtn(true), 1000);
        } else {
          setProgress(newProgress);
        }
      }, interval);

      return () => clearInterval(timer);
    } else {
      setShowCloseBtn(false);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950 flex flex-col items-center justify-center p-6 animate-in fade-in duration-500">
      <div className="w-full max-w-lg flex flex-col items-center">
        <h3 className="text-4xl md:text-5xl font-medium text-white mb-16 tracking-[-0.04em] text-center">
          {messageKey ? t(messageKey, fallbackMessage) : fallbackMessage}
        </h3>

        <div className="w-full max-w-md space-y-6">
          <div className="relative w-full h-[6px] bg-white/10 rounded-full overflow-visible">
            <div 
              className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all duration-75 ease-linear"
              style={{ 
                width: `${progress}%`,
                boxShadow: '0 0 20px rgba(79, 70, 229, 0.4)'
              }}
            >
              {/* Glow Tip */}
              <div 
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 bg-primary/40 blur-xl rounded-full"
              />
              <div 
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-white/80 blur-[2px] rounded-full shadow-[0_0_10px_#fff]"
              />
            </div>
          </div>
          
          <div className="text-center space-y-6">
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-white/40">
              {t('common.percentComplete', { percent: Math.round(progress) })}
            </p>
            {showCloseBtn && (
              <div className="flex justify-center animate-in fade-in zoom-in-95 duration-500">
                <button
                  onClick={() => {
                    setIsVisible(false);
                    window.location.href = '/settings/translation';
                  }}
                  className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-medium text-xs transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer flex items-center gap-2 border border-white/10 hover:border-white/20 shadow-lg opacity-50"
                >
                  <ChevronLeft size={16} />
                  {t('common.back', 'voltar')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Pré-carrega o perfil do usuário logo após o login, para que /profile abra instantaneamente
const ProfilePrefetcher: React.FC = () => {
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.id) return;
    const timer = setTimeout(() => {
      import('./services/profilePrefetch').then(m => m.prefetchOwnProfile(user));
    }, 1500);
    return () => clearTimeout(timer);
  }, [user?.id]);
  return null;
};

const StreakManager: React.FC = () => {
  const { user } = useAuth();
  const { refreshProfile } = useData();

  useEffect(() => {
    const handleStatsUpdate = async () => {
      if (!user) return;
      // Increment streak whenever any statistic is updated
      const result = await streakService.validateAndIncrementStreak(user.id, 'other');
      if (result && result.newlyIncremented) {
        window.dispatchEvent(new CustomEvent('libritui-streak-updated', { 
          detail: result 
        }));
        refreshProfile();
      }
    };

    window.addEventListener('libritui-stats-updated', handleStatsUpdate);
    return () => window.removeEventListener('libritui-stats-updated', handleStatsUpdate);
  }, [user, refreshProfile]);

  return null;
};

const AuthHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile } = useData();
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    locationRef.current = location.pathname;
  });

  useEffect(() => {
    // Detect Stripe Checkout Session
    const params = new URLSearchParams(window.location.search);
    if (params.has('session_id')) {
      console.log('[AuthHandler] Stripe session detected, forcing profile refresh...');
      refreshProfile(true);
      
      // Clean up the URL to prevent stale data on reload
      params.delete('session_id');
      const newSearch = params.toString();
      const newPath = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState(null, '', newPath);
    }

    const handleInitialSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        const currentPath = locationRef.current;
        if (currentPath === '/login') {
          navigate('/', { replace: true });
        }
      }
    };

    handleInitialSession();

    let hashTimer: NodeJS.Timeout;
    if (window.location.hash.includes('access_token') || window.location.hash.includes('error')) {
      hashTimer = setTimeout(() => {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }, 500);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const currentPath = locationRef.current;
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        if (currentPath === '/login') {
          navigate('/', { replace: true });
        }
      }

      if (event === 'SIGNED_OUT' && currentPath !== '/login') {
        navigate('/login', { replace: true });
      }
    });

    return () => {
      subscription.unsubscribe();
      if (hashTimer) clearTimeout(hashTimer);
    };
  }, [navigate]);

  return null;
};

const ActiveStudyTrackerListener: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { profile } = useData();

  useEffect(() => {
    activeStudyTracker.init();
  }, []);

  useEffect(() => {
    const langCode = profile?.studyLanguage || profile?.targetLanguage || 'all';
    activeStudyTracker.setUserContext(user?.id || null, langCode);
    activeStudyTracker.onRouteChange(location.pathname, user?.id || undefined, langCode);
  }, [location.pathname, user?.id, profile]);

  return null;
};

// /quizzes é um atalho para a trilha de gramática (cada módulo traz explicação + quiz)
const QuizzesRedirect: React.FC = () => {
  const { langCode, level, topicId } = useParams();
  const location = useLocation();
  const path = ['/grammar', langCode || 'en', level || 'a1', topicId].filter(Boolean).join('/');
  return <Navigate to={`${path}${location.search}`} replace />;
};

const ReaderRouter: React.FC = () => {
  const { id } = useParams();
  const { books } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [showImportModal, setShowImportModal] = useState(location.state?.showImportModal || false);
  const isDriveLinked = location.state?.isDriveLinked || false;

  const mimeType = localStorage.getItem(`mime_${id}`);
  const currentBook = books.find(b => b.id === id || b.custom_id === id);

  const isNumericId = id && /^\d+$/.test(id);
  const isHtmlFormat =
    !!isNumericId ||
    id?.startsWith('gut-') ||
    id?.startsWith('club_') ||
    currentBook?.format === 'html' ||
    currentBook?.format === 'epub' ||
    mimeType === 'text/html' ||
    mimeType === 'application/epub+zip' ||
    mimeType?.includes('html') ||
    mimeType?.includes('epub');

  const importTranslations: Record<string, { noDrive: string, withDrive: string, confirm: string, link: string }> = {
    pt: {
      noDrive: 'Livro importado com sucesso! Este livro está salvo localmente. Para sincronizar seus livros pessoais e acessá-los de qualquer dispositivo, vincule uma conta Google em "ajustes".',
      withDrive: 'Livro importado com sucesso! Este livro está salvo na nuvem e você pode acessá-lo de qualquer dispositivo.',
      confirm: 'Confirmar',
      link: 'Vincular conta'
    },
    en: {
      noDrive: 'Book imported successfully! This book is saved locally. To sync your personal books and access them from any device, link a Google account in "settings".',
      withDrive: 'Book imported successfully! This book is saved in the cloud and you can access it from any device.',
      confirm: 'Confirm',
      link: 'Link account'
    },
    es: {
      noDrive: '¡Libro importado con éxito! Este libro está guardado localmente. Para sincronizar tus libros personales y acceder a ellos desde cualquier dispositivo, vincula una cuenta de Google en "ajustes".',
      withDrive: '¡Libro importado con éxito! Este libro está guardado en la nube y puedes acceder a él desde cualquier dispositivo.',
      confirm: 'Confirmar',
      link: 'Vincular cuenta'
    },
    fr: {
      noDrive: 'Livre importé avec succès! Ce livre est enregistré localement. Pour synchroniser vos livres personnels et y accéder depuis n\'importe quel appareil, associez un compte Google dans les "paramètres".',
      withDrive: 'Livre importé avec succès! Ce livre est enregistré dans le cloud et vous pouvez y accéder depuis n\'importe quel appareil.',
      confirm: 'Confirmer',
      link: 'Associer un compte'
    },
    it: {
      noDrive: 'Libro importato con successo! Questo libro è salvato localmente. Per sincronizzare i tuoi libri personali e accedervi da qualsiasi dispositivo, collega un account Google nelle "impostazioni".',
      withDrive: 'Libro importato con successo! Questo libro è salvato nel cloud e puoi accedervi da qualsiasi dispositivo.',
      confirm: 'Conferma',
      link: 'Collega account'
    },
    de: {
      noDrive: 'Buch erfolgreich importiert! Dieses Buch ist lokal gespeichert. Um Ihre persönlichen Bücher zu synchronisieren und von jedem Gerät aus darauf zuzugreifen, verknüpfen Sie ein Google-Konto in den "Einstellungen".',
      withDrive: 'Buch erfolgreich importiert! Dieses Buch ist in der Cloud gespeichert und Sie können von jedem Gerät aus darauf zugreifen.',
      confirm: 'Bestätigen',
      link: 'Konto verknüpfen'
    }
  };

  const activeLang = i18n.language?.split('-')[0] || 'pt';
  const tr = importTranslations[activeLang] || importTranslations.pt;

  const handleLinkAccount = () => {
    setShowImportModal(false);
    navigate('/settings');
    // Note: Tab routing is usually handled inside settings, setting timeout if needed. We'll navigate simply to /settings. Or we can set a localStorage flag if tab query params are not used. Wait, I'll pass state.
    setTimeout(() => {
       const tabElement = document.querySelector('[label="Google Drive"]');
       if(tabElement) (tabElement as HTMLElement).click();
    }, 100);
  };

  const handleClose = () => {
    setShowImportModal(false);
  };

  return (
    <>
      {isHtmlFormat ? <LibriTuiReaderPage /> : <ReaderPage fileUrl={id!} />}
      {showImportModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={handleClose}>
          <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 max-w-md w-full animate-in zoom-in-95 duration-200 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-20 h-20 bg-tint-sage rounded-full mx-auto flex items-center justify-center text-ok mb-6">
              <CheckCircle2 size={40} />
            </div>
            <h3 className="text-xl font-medium text-ink mb-4">
              {isDriveLinked ? 'Sincronizado!' : 'Sucesso!'}
            </h3>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">
              {isDriveLinked ? tr.withDrive : tr.noDrive}
            </p>
            <div className="flex flex-col gap-3">
              <button onClick={handleClose} className="w-full py-4 bg-ink text-surface rounded-2xl font-medium text-[10px] hover:scale-[1.02] active:scale-95 transition-all">
                {tr.confirm}
              </button>
              {!isDriveLinked && (
                <button onClick={handleLinkAccount} className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-medium text-[10px] border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">
                  {tr.link}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const StreakHistoryModal: React.FC<{ isOpen: boolean; onClose: () => void; currentStreak: number }> = ({ isOpen, onClose, currentStreak }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 max-w-sm w-full animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-tint-butter rounded-2xl flex items-center justify-center text-warn">
              <Trophy size={24} />
            </div>
            <div>
              <h3 className="text-xl font-medium text-ink leading-tight">{t('common.streak_history')}</h3>
              <p className="text-xs text-warn font-bold">{t('common.days_streak', { count: currentStreak })}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-800">
            <h4 className="text-sm font-medium text-ink mb-3">{t('common.current_status')}</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-tint-sage flex items-center justify-center">
                <CheckCircle2 size={20} className="text-ok" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{t('common.active_streak')}</p>
                <p className="text-xs text-slate-500">{t('common.streak_message')}</p>
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-full py-3.5 mt-8 bg-ink text-surface rounded-full font-medium text-sm hover:scale-[1.02] active:scale-95 transition-all"
        >
          {t('common.confirm', 'Confirmar')}
        </button>
      </div>
    </div>
  );
};

const formatStreak = (streak: number) => {
  if (streak >= 1000) {
    const k = Math.floor(streak / 1000);
    const rest = streak % 1000;
    return `${k}k${rest > 0 ? rest : ''}`;
  }
  return streak.toString();
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { profile } = useData();
  const location = useLocation();
  const [isStreakModalOpen, setIsStreakModalOpen] = useState(false);

  if (loading) return <LoadingOrError />;

  if (!user) return <Navigate to="/login" replace />;

  const isOrbPage = location.pathname === '/' || location.pathname.endsWith('-hub') || location.pathname.startsWith('/grammar');

  return (
    <div className="h-screen h-[100dvh] w-full flex flex-col bg-slate-50 dark:bg-slate-950 transition-colors duration-500 overflow-hidden">
      <MissionProgressBar />

      <main className="flex-1 overflow-y-auto overflow-x-hidden w-full custom-scrollbar touch-pan-y relative pb-safe" style={{ paddingTop: 'var(--mission-h, 0px)', transition: 'padding-top 0.3s ease' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={`mx-auto w-full h-full ${isOrbPage ? 'p-0 max-w-none' : 'p-4 md:p-6 lg:p-10 max-w-7xl'}`}
          >
            <GlobalErrorBoundary resetKey={location.pathname}>
              {React.isValidElement(children) ? React.cloneElement(children as React.ReactElement, { location, key: location.pathname }) : children}
            </GlobalErrorBoundary>
          </motion.div>
        </AnimatePresence>
      </main>

      <StreakHistoryModal
        isOpen={isStreakModalOpen}
        onClose={() => setIsStreakModalOpen(false)}
        currentStreak={profile?.current_streak || 0}
      />

      <BottomNavigationBar />
    </div>
  );
};

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/', { replace: true });
      }
    });
  }, [navigate]);

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Em desenvolvimento (npm run dev) volta para o próprio localhost; produção inalterada
        redirectTo: import.meta.env.DEV ? window.location.origin : 'https://www.libritui.com',
        queryParams: { prompt: 'select_account' }
      }
    });
    if (error) alert('Erro ao entrar com Google: ' + error.message);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-surface p-8 md:p-12 rounded-[28px] text-center space-y-10">
        <div className="w-20 h-20 md:w-24 md:h-24 bg-primary rounded-full mx-auto flex items-center justify-center text-white">
          <BookText className="w-10 h-10 md:w-12 md:h-12" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-medium dark:text-white tracking-[-0.04em]">LibriTui</h1>
          <p className="text-slate-500 text-[9px] md:text-[10px] font-medium uppercase mt-2 tracking-[0.2em]">{t('auth.tagline')}</p>
        </div>
        <div className="space-y-4">
          <button
            onClick={handleGoogleLogin}
            className="w-full py-4 md:py-5 bg-white text-slate-900 border border-slate-200 rounded-full font-medium text-sm flex items-center justify-center gap-4 hover:scale-[1.02] active:scale-95 transition-all"
          >
            <img src="https://www.google.com/favicon.ico" className="w-5 h-5" alt="Google" />
            {t('auth.login_google')}
          </button>

          {/* Somente em desenvolvimento (npm run dev): visualizar o layout sem conta.
              Em produção o Vite remove este bloco inteiro (import.meta.env.DEV = false). */}
          {import.meta.env.DEV && (
            <button
              onClick={() => {
                localStorage.setItem('libritui_user', JSON.stringify({
                  id: 'dev-guest', email: 'convidado@localhost', displayName: 'Convidado', photoURL: ''
                }));
                window.location.href = '/';
              }}
              className="w-full py-4 rounded-full border border-dashed border-slate-400/40 text-slate-400 font-medium text-sm hover:text-white hover:border-slate-300/60 transition-colors"
            >
              Entrar como convidado <span className="opacity-60">(só localhost)</span>
            </button>
          )}

          <div className="pt-4 text-[10px] text-slate-400 dark:text-slate-500 font-medium leading-relaxed px-4">
            <p>
              Ao avançar, você concorda com os nossos <Link to="/terms" className="underline hover:text-slate-300 transition-colors">Termos de Serviço</Link> e <Link to="/privacy" className="underline hover:text-slate-300 transition-colors">Política de Privacidade</Link>.
            </p>
            <p className="mt-2 opacity-80">
              By proceeding, you agree to our <Link to="/terms" className="underline hover:text-slate-300 transition-colors">Terms of Service</Link> and <Link to="/privacy" className="underline hover:text-slate-300 transition-colors">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpdateNotifier: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { accentColor } = useTheme();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Check if we just returned from a manual update to show the success modal
    if (localStorage.getItem('libritui_update_success_pending') === 'true') {
      setShowSuccess(true);
      localStorage.removeItem('libritui_update_success_pending');
    }

    // ONLY show after login
    if (!user) {
      setShow(false);
      return;
    }

    const lastAcceptedVersion = localStorage.getItem('last_accepted_version');
    if (lastAcceptedVersion !== APP_VERSION) {
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleUpdate = () => {
    setShow(false);
    navigate('/settings/updates');
  };

  const handleConfirmUpdate = () => {
    setShowSuccess(false);
  };

  if (!show && !showSuccess) return null;

  return (
    <>
      {show && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-surface border border-black/[0.06] dark:border-white/[0.06] p-6 rounded-[2rem] max-w-sm animate-in slide-in-from-bottom-5 duration-500">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-medium text-lg text-ink flex items-center gap-2">
              <RotateCw className="text-warn" size={20} /> {t('common.update_title')}
            </h3>
            <button onClick={() => { setShow(false); localStorage.setItem('last_accepted_version', APP_VERSION); }} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400 transition-colors">
              <X size={16} />
            </button>
          </div>
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-5 leading-relaxed">
            {t('common.update_desc')}
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => { setShow(false); localStorage.setItem('last_accepted_version', APP_VERSION); }} className="px-5 py-2.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
              {t('common.later')}
            </button>
            <button 
              onClick={handleUpdate}
              className="px-5 py-2.5 text-xs font-medium bg-ink text-surface rounded-xl transition-all hover:scale-105 active:scale-95 shadow-md"
            >
              Atualizar
            </button>
          </div>
        </div>
      )}

      <UpdateSuccessModal
        isOpen={showSuccess}
        onConfirm={handleConfirmUpdate}
      />
    </>
  );
};

const PersonalHubRoute: React.FC = () => {
  const { t } = useTranslation();
  const { profile, refreshProfile } = useData();
  const { user } = useAuth();

  // Fetch obrigatório no D1 toda vez que a página for montada
  useEffect(() => {
    refreshProfile(true);
    import('./services/prefetchService').then(m => m.prefetchService.prefetchPersonal(user?.id));
  }, [user?.id]);

  return (
    <HubPage 
      title={t('hubs.personal.title')} 
      subtitle={t('hubs.personal.subtitle')}
      options={[
        { 
          label: t('hubs.personal.profile.label'), 
          description: t('hubs.personal.profile.description'), 
          icon: <Fingerprint />, 
          path: profile?.username ? `/profile/${profile.username}` : '/profile', 
          color: 'orange-500',
          onClick: refreshProfile,
          tourId: 'tour-personal-profile'
        },
        { 
          label: t('hubs.personal.level_test.label', 'Meu nível'), 
          description: t('hubs.personal.level_test.description', 'Teste e avalie o seu vocabulário passivo'), 
          icon: <BrainCircuit />, 
          path: '/personal-hub/level-test', 
          color: 'orange-500',
          tourId: 'tour-personal-level-test'
        },
        { 
          label: t('statistics.title'), 
          description: t('hubs.personal.statistics.description', 'Visualize seu progresso diário e mensal'), 
          icon: <BarChart2 />, 
          path: '/reports', 
          color: 'orange-500',
          tourId: 'tour-personal-stats'
        },
        { label: t('hubs.personal.achievements.label'), description: t('hubs.personal.achievements.description'), icon: <Trophy />, path: '/achievements', color: 'orange-500', tourId: 'tour-personal-achievements' },
        { label: t('hubs.personal.settings.label'), description: t('hubs.personal.settings.description'), icon: <Settings />, path: '/settings', color: 'orange-500', tourId: 'tour-personal-settings' },
      ]}
      tourPhaseName="PERSONAL_HUB"
    />
  );
};

const LibraryHubRoute = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { refreshBooks } = useData();

  return (
    <HubPage 
      title={t('hubs.library.title')} 
      subtitle={t('hubs.library.subtitle')}
      onMount={() => {
        refreshBooks();
        import('./services/prefetchService').then(m => m.prefetchService.prefetchLibrary(user?.id));
      }}
      options={[
        { 
          label: t('hubs.library.shelf.label'), 
          description: t('hubs.library.shelf.description'), 
          icon: <Library />, 
          path: '/library', 
          color: 'amber-500',
          tourId: 'tour-library-shelf',
          onClick: () => {
             if (tourService.isActive() && tourService.getPhase() === 'LIBRARY_HUB') {
                tourService.setPhase('LIBRARY');
             }
          }
        },
        { label: t('hubs.library.online.label'), description: t('hubs.library.online.description'), icon: <BookOpen />, path: '/libritui-library', color: 'amber-500', tourId: 'tour-library-online' },
        { label: t('hubs.library.audiobooks.label'), description: t('hubs.library.audiobooks.description'), icon: <Headphones />, path: '/audiobooks', color: 'amber-500', tourId: 'tour-library-audio' },
      ]}
      tourPhaseName="LIBRARY_HUB"
    />
  );
};

const getStudyHubTranslations = (lng: string) => {
  const trans: Record<string, { label: string; description: string }> = {
    pt: { label: "Gramática", description: "Explore a estrutura do idioma" },
    en: { label: "Grammar", description: "Explore the language structure" },
    es: { label: "Gramática", description: "Explora la estructura del idioma" },
    fr: { label: "Grammaire", description: "Explorez la structure de la langue" },
    it: { label: "Grammatica", description: "Esplora la struttura della lingua" },
    de: { label: "Grammatik", description: "Entdecken Sie die Struktur der Sprache" },
    hu: { label: "Nyelvtan", description: "Fedezze fel a nyelv szerkezetét" },
    ja: { label: "文法", description: "言語の構造を探索する" },
    zh: { label: "语法", description: "探索语言结构" },
    ru: { label: "Грамматика", description: "Изучите структуру языка" }
  };
  const cleanLng = (lng || 'pt').split('-')[0].toLowerCase();
  return trans[cleanLng] || trans.pt;
};

const StudyHubRoute = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const grammarTrans = getStudyHubTranslations(i18n.language);

  return (
    <HubPage 
        title={t('hubs.study.title')} 
        subtitle={t('hubs.study.subtitle')}
        onMount={() => import('./services/prefetchService').then(m => m.prefetchService.prefetchStudy(user?.id))}
        options={[
          { label: t('hubs.study.flashcards.label'), description: t('hubs.study.flashcards.description'), icon: <Brain />, path: '/flashcards', color: 'blue-500', tourId: 'tour-study-flash' },
          { label: 'Shadowing', description: 'Pratique a pronúncia e entonação em tempo real com áudio nativo', icon: <Volume2 />, path: '/shadowing', color: 'blue-500', tourId: 'tour-study-shadowing' },
          { label: t('hubs.study.pronunciation.label'), description: t('hubs.study.pronunciation.description'), icon: <Mic2 />, path: '/pronunciation', color: 'blue-500', tourId: 'tour-study-pronunciation' },
          { label: t('hubs.study.writing.label'), description: t('hubs.study.writing.description'), icon: <PenLine />, path: '/writing', color: 'blue-500', tourId: 'tour-study-writing' },
          { label: 'Listening', description: 'Ouça frases em áudio e pratique sua compreensão auditiva', icon: <Headphones />, path: '/listening', color: 'blue-500', tourId: 'tour-study-listening' },
          { label: grammarTrans.label, description: grammarTrans.description, icon: <BookOpen />, path: '/grammar/en/a1', color: 'blue-500', tourId: 'tour-study-grammar' },
          { label: t('hubs.study.voz_ativa.label', 'Voz Ativa'), description: t('hubs.study.voz_ativa.description', 'Pratique conversação fluida com IA'), icon: <Mic />, path: '/active-voice', color: 'blue-500', tourId: 'tour-study-voz' },
        ]}
        tourPhaseName="STUDY_HUB"
      />
  );
};

const CommunityHubRoute = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  return (
    <HubPage 
      title={t('hubs.community.title')} 
      subtitle={t('hubs.community.subtitle')}
      onMount={() => import('./services/prefetchService').then(m => m.prefetchService.prefetchCommunity(user?.id))}
      options={[
        { label: t('hubs.community.friends.label'), description: t('hubs.community.friends.description'), icon: <COMMUNITY_ICONS.friends.Icon />, path: '/friends', color: 'rose-500', tourId: 'tour-community-friends' },
        { label: t('hubs.community.chat.label'), description: t('hubs.community.chat.description'), icon: <COMMUNITY_ICONS.chat.Icon />, path: '/chat', color: 'rose-500', tourId: 'tour-community-chat' },
        { label: t('hubs.community.feed.label', 'Comunidade'), description: t('hubs.community.feed.description', 'Veja citações e posts de amigos'), icon: <COMMUNITY_ICONS.feed.Icon />, path: '/community-feed', color: 'rose-500', tourId: 'tour-community-feed' },
        { label: t('hubs.community.clubs.label'), description: t('hubs.community.clubs.description'), icon: <COMMUNITY_ICONS.clubs.Icon />, path: '/book-club', color: 'rose-500', tourId: 'tour-community-clubs' },
      ]}
      tourPhaseName="COMMUNITY_HUB"
    />
  );
};

const App: React.FC = () => {
  const { t } = useTranslation();
  useEffect(() => {
    // Sinaliza ao index.html que o React montou: telas que só usam portal (ex.: /sharing)
    // deixam o #root vazio e não podem disparar a tela de erro estática.
    (window as any).__libriTuiMounted = true;
    import('./services/supabaseClient').then(({ supabase }) => {
      console.log('Supabase client initialized:', supabase);
    }).catch(err => console.error('Failed to initialize Supabase:', err));

    import('./services/WhisperSTTService').then(({ whisperSTTService }) => {
      whisperSTTService.prefetchModel();
    }).catch(err => console.error('Failed to prefetch Whisper STT model:', err));

    // Dicionário só fica no aparelho se o usuário PRO baixou em /settings/translation
    import('./services/dictionaryOfflineService').then(({ purgeAutoDownloadedDicts }) => purgeAutoDownloadedDicts()).catch(() => {});
    import('./services/translationService').then(({ prefetchFreeDictCache }) => {
      prefetchFreeDictCache();
    }).catch(() => {});
  }, []);

  useVersionCheck();


  const [isStreakCongratOpen, setIsStreakCongratOpen] = useState(false);
  const [currentStreakVal, setCurrentStreakVal] = useState(0);

  useEffect(() => {
    const handleStreakUpdate = (e: any) => {
      const { newlyIncremented, streak } = e.detail;
      if (newlyIncremented) {
        setCurrentStreakVal(streak);
        setIsStreakCongratOpen(true);

        // Notify friends on streak milestones (1, 2, 3, 5, 10, 15, 20, 25...)
        supabase.auth.getUser().then(({ data }) => {
          if (data?.user?.id) {
            notificationService.notifyFriendsStreakMilestone(data.user.id, streak);
          }
        }).catch(err => console.warn('[App] Failed to notify friends of streak:', err));
      }
    };
    window.addEventListener('libritui-streak-updated', handleStreakUpdate);
    return () => window.removeEventListener('libritui-streak-updated', handleStreakUpdate);
  }, []);

  return (
    <AuthProvider>
      <DataProvider>
        <ThemeProvider>
          <GlobalErrorBoundary>
            <Router>
            <UnifiedDictionaryProvider>
              <SyncedAudiobookProvider>
                <StreakCongratulationModal 
                  isOpen={isStreakCongratOpen} 
                  onClose={() => setIsStreakCongratOpen(false)} 
                  streak={currentStreakVal} 
                />
                <TourOverlay />
                  <React.Suspense fallback={<LoadingOrError label="LibriTui" />}>
                    <UpdateNotifier />

                    <ReloadPrompt />
                    <LanguagePickerModal />
                    <AuthHandler />
                    <StreakManager />
                    <ProfilePrefetcher />
                    <GlobalLoadingOverlay />
                    <SecurityGuard />
                    <PeriodReportCycleNotification />
                    <ActiveStudyTrackerListener />
                    <OfflineTranslatorPromptModal />
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/reader/:id" element={<Layout><ReaderRouter /></Layout>} />
                      <Route path="/preview/:id" element={<Layout><PreviewReader /></Layout>} />
                      <Route path="/sharing" element={<SharingPage />} />
                      <Route path="/" element={<Layout><HomeOrbMenu /></Layout>} />
                      <Route path="/reports" element={<Layout><StatisticsPage /></Layout>} />
                      <Route path="/library-hub" element={<Layout><LibraryHubRoute /></Layout>} />
                      <Route path="/study-hub" element={<Layout><StudyHubRoute /></Layout>} />
                      <Route path="/shadowing" element={<Layout><ShadowingPage /></Layout>} />
                      <Route path="/community-hub" element={<Layout><CommunityHubRoute /></Layout>} />
                      <Route path="/personal-hub" element={<Layout><PersonalHubRoute /></Layout>} />
                      <Route path="/level-test" element={<Layout><LevelTestPage /></Layout>} />
                      <Route path="/personal-hub/level-test" element={<Navigate to="/level-test" replace />} />
                      <Route path="/help" element={<Layout><HelpPage /></Layout>} />
                      <Route path="/onboarding" element={<Layout><OnboardingPage /></Layout>} />
                      <Route path="/library" element={<Layout><HomePage /></Layout>} />
                      <Route path="/flashcards" element={<Layout><FlashcardsPage /></Layout>} />
                      <Route path="/flashcards/guide/:langCode?/:level?" element={<Layout><FlashcardsPage /></Layout>} />
                      <Route path="/flashcards/guide" element={<Layout><FlashcardsPage /></Layout>} />
                      <Route path="/flashcards/:langCode/:level?" element={<Layout><FlashcardsPage /></Layout>} />
                      <Route path="/flashcards/store" element={<Layout><FlashcardStorePage /></Layout>} />
                      <Route path="/flashcards/store/select/:nativeLang/:targetLang" element={<Layout><FlashcardStoreSelectPage /></Layout>} />
                      <Route path="/flashcards/store/select" element={<Navigate to="/flashcards/store" replace />} />
                      <Route path="/audiobooks" element={<Layout><AudiobooksPage /></Layout>} />
                      <Route path="/audiobooks/library" element={<Layout><AudiobooksPage /></Layout>} />
                      <Route path="/audiobooks/:id" element={<Layout><AudiobookDetailPage /></Layout>} />
                      <Route path="/libritui-library" element={<Layout><LibriTuiLibraryPage /></Layout>} />
                      <Route path="/grammar" element={<Navigate to="/grammar/en/a1" replace />} />
                      <Route path="/quizzes" element={<QuizzesRedirect />} />
                      <Route path="/quizzes/:langCode/:level?/:topicId?" element={<QuizzesRedirect />} />
                      <Route path="/grammar/guide/:langCode/:level?/:topicId?" element={<Layout><GrammarPage /></Layout>} />
                      <Route path="/grammar/:langCode/:level?/:topicId?" element={<Layout><GrammarPage /></Layout>} />
                      <Route path="/roadmap" element={<Layout><RoadmapPage /></Layout>} />
                      <Route path="/roadmap/:langCode/:level" element={<Layout><RoadmapPage /></Layout>} />
                      <Route path="/correction" element={<Layout><ReviewErrorsPage /></Layout>} />
                      <Route path="/review-errors" element={<Layout><ReviewErrorsPage /></Layout>} />
                      <Route path="/review-errors/guide" element={<Layout><ReviewErrorsPage /></Layout>} />
                      <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
                      <Route path="/profile/:username" element={<Layout><ProfilePage /></Layout>} />
                      <Route path="/profile/:username/:postId" element={<Layout><ProfilePage /></Layout>} />
                      <Route path="/settings" element={<Layout><SettingsPage /></Layout>} />
                      <Route path="/settings/beta" element={<Layout><BetaSyncPage /></Layout>} />
                      <Route path="/settings/beta/sync-audiobooks" element={<Layout><BetaSyncPage /></Layout>} />
                      <Route path="/settings/beta/tests-align" element={<Layout><BetaSyncPage /></Layout>} />
                      <Route path="/settings/:tab" element={<Layout><SettingsPage /></Layout>} />
                      <Route path="/pronunciation" element={<Layout><PronunciationPage /></Layout>} />
                      <Route path="/pronunciation/guide/:langCode?/:level?" element={<Layout><PronunciationPage /></Layout>} />
                      <Route path="/pronunciation/guide" element={<Layout><PronunciationPage /></Layout>} />
                      <Route path="/pronunciation/:langCode/:level?" element={<Layout><PronunciationPage /></Layout>} />
                      <Route path="/writing" element={<Layout><WritingPage /></Layout>} />
                      <Route path="/writing/guide/:langCode?/:level?" element={<Layout><WritingPage /></Layout>} />
                      <Route path="/writing/guide" element={<Layout><WritingPage /></Layout>} />
                      <Route path="/writing/:langCode/:level?" element={<Layout><WritingPage /></Layout>} />
                      <Route path="/listening" element={<Layout><ListeningPage /></Layout>} />
                      <Route path="/listening/guide/:langCode?/:level?" element={<Layout><ListeningPage /></Layout>} />
                      <Route path="/listening/guide" element={<Layout><ListeningPage /></Layout>} />
                      <Route path="/listening/:langCode/:level?" element={<Layout><ListeningPage /></Layout>} />
                      <Route path="/friends" element={<Layout><FriendsPage /></Layout>} />
                      <Route path="/estudo/voz-ativa" element={<Navigate to="/active-voice" replace />} />
                      <Route path="/active-voice" element={<Layout><ActiveVoicePage /></Layout>} />
                      <Route path="/active-voice/:deckId" element={<Layout><VozAtivaPage /></Layout>} />
                      <Route path="/chat" element={<Layout><ChatPage /></Layout>} />
                      <Route path="/achievements" element={<Layout><AchievementsPage /></Layout>} />
                      <Route path="/comunidade" element={<Navigate to="/book-club" replace />} />
                      <Route path="/book-club" element={<Layout><CommunityPage /></Layout>} />
                      <Route path="/community-feed/:postId?" element={<Layout><CommunityFeedPage /></Layout>} />
                      <Route path="/statistics" element={<Navigate to="/reports" replace />} />
                      <Route path="/notifications" element={<Layout><NotificationsPage /></Layout>} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </React.Suspense>
              </SyncedAudiobookProvider>
            </UnifiedDictionaryProvider>
            </Router>
          </GlobalErrorBoundary>
        </ThemeProvider>
      </DataProvider>
    </AuthProvider>
  );
};

export default App;
